import { Menu, MessageCircle } from 'lucide-react'
import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { AppSidebar } from '@/components/AppSidebar'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useMaintenance } from '@/hooks/use-maintenance'
import { usePublishOrganizationInUrlPolicy } from '@/hooks/use-organization-in-url-policy'
import { usePageTitle } from '@/hooks/use-page-title'
import { useTabOrganizationChangeNotice } from '@/hooks/use-tab-organization-change-notice'
import { useTranslation } from '@/hooks/use-translation'
import { useImpersonationStatus } from '@/hooks/use-impersonation-status'
import { AgentChatPanel } from '@/features/agent/components/agent-chat-panel'

export function AppLayout() {
  const result = usePageTitle({ returnTitle: true })
  const t = useTranslation()
  const title = result?.title ?? t('sidebar.appName')
  const { isImpersonating } = useImpersonationStatus()
  const [isBannerMinimized, setIsBannerMinimized] = useState(false)
  const [isAgentOpen, setIsAgentOpen] = useState(false)
  const isAgentEnabled = useFeatureFlag('referent_agent_ui')
  useMaintenance()
  usePublishOrganizationInUrlPolicy()
  useTabOrganizationChangeNotice()

  return (
    <SidebarProvider>
      <ImpersonationBanner
        minimized={isBannerMinimized}
        onMinimize={() => setIsBannerMinimized(true)}
        onExpand={() => setIsBannerMinimized(false)}
      />
      <div
        className={`flex h-dvh w-dvw overflow-hidden ${
          isImpersonating && !isBannerMinimized ? 'pt-12' : ''
        }`}
      >
        <AppSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="h-14 shrink-0 border-b border-neutral-200 bg-white flex items-center justify-between gap-2 px-3">
            <div className="flex items-center gap-2">
              <SidebarTrigger>
                <Button variant="ghost" size="icon" aria-label={t('sidebar.toggleSidebar')}>
                  <Menu className="h-5 w-5" />
                </Button>
              </SidebarTrigger>
              <span className="font-medium text-neutral-700">{title}</span>
            </div>
            <div className="flex items-center gap-2">
              {isAgentEnabled && (
                <Button
                  size="sm"
                  onClick={() => setIsAgentOpen(true)}
                  aria-label={t('sidebar.openSergeo')}
                  aria-expanded={isAgentOpen}
                  className="h-9 bg-gradient-to-br from-primary to-secondary text-primary-foreground hover:opacity-90"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('sidebarNav.sergeo')}</span>
                </Button>
              )}
              <LanguageSwitcher />
            </div>
          </header>
          <main className="flex-1 min-h-0 min-w-0 overflow-auto bg-neutral-50">
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster richColors />

      {isAgentEnabled && isAgentOpen && (
        <AgentChatPanel onClose={() => setIsAgentOpen(false)} />
      )}
    </SidebarProvider>
  )
}
